/**
 * addressUtils.js — shared country / province helpers for checkout & profile forms
 */

export const CA_PROVINCES = [
  { name: 'Alberta', code: 'AB' },
  { name: 'British Columbia', code: 'BC' },
  { name: 'Manitoba', code: 'MB' },
  { name: 'New Brunswick', code: 'NB' },
  { name: 'Newfoundland and Labrador', code: 'NL' },
  { name: 'Nova Scotia', code: 'NS' },
  { name: 'Ontario', code: 'ON' },
  { name: 'Prince Edward Island', code: 'PE' },
  { name: 'Quebec', code: 'QC' },
  { name: 'Saskatchewan', code: 'SK' },
  { name: 'Northwest Territories', code: 'NT' },
  { name: 'Nunavut', code: 'NU' },
  { name: 'Yukon', code: 'YT' },
];

const CA_NAME_TO_CODE = Object.fromEntries(
  CA_PROVINCES.flatMap(({ name, code }) => [
    [name.toLowerCase(), code],
    [code.toLowerCase(), code],
  ])
);

/** Labels that must not auto-fill international Country Name */
const DOMESTIC_OR_PLACEHOLDER_LABELS = new Set([
  'canada',
  'ca',
  'united states',
  'us',
  'usa',
  'international',
  'international (all other countries)',
]);

export function isDomesticOrPlaceholderCountryLabel(value = '') {
  return DOMESTIC_OR_PLACEHOLDER_LABELS.has(String(value || '').trim().toLowerCase());
}

/** Country name for INTL orders — never default from CA/US dropdown labels */
export function resolveInternationalCountryName(address = {}) {
  const explicit = String(address.countryName || '').trim();
  if (explicit && !isDomesticOrPlaceholderCountryLabel(explicit)) return explicit;

  const fromCountry = String(address.country || '').trim();
  if (fromCountry && !isDomesticOrPlaceholderCountryLabel(fromCountry)) return fromCountry;

  return '';
}

/** Normalize country string/code to CA | US | INTL */
export function resolveCountryCode(address = {}) {
  // Trust explicit dropdown selection first — stale country labels must not override INTL
  const explicit = (address.countryCode || '').toUpperCase();
  if (explicit === 'CA' || explicit === 'US' || explicit === 'INTL') {
    return explicit;
  }

  const country = (address.country || address.countryName || '').trim().toLowerCase();
  if (country === 'canada' || country === 'ca') return 'CA';
  if (country === 'united states' || country === 'us' || country === 'usa') return 'US';

  if (!country) return 'CA';
  return 'INTL';
}

export function resolveCountryLabel(address = {}, countryCode = resolveCountryCode(address)) {
  if (countryCode === 'CA') return 'Canada';
  if (countryCode === 'US') return 'United States';
  return (address.countryName || address.country || '').trim();
}

/** Map full province name or lowercase code to standard 2-letter code */
export function normalizeCanadianProvince(value) {
  const raw = (value || '').trim();
  if (!raw) return '';
  const mapped = CA_NAME_TO_CODE[raw.toLowerCase()];
  if (mapped) return mapped;
  return raw.length <= 3 ? raw.toUpperCase() : raw;
}

export function getStateProvinceValue(address = {}, countryCode = resolveCountryCode(address)) {
  if (countryCode === 'CA') return address.province || '';
  if (countryCode === 'US') return address.state || address.province || '';
  return address.region || address.province || address.state || '';
}

export function normalizeAddress(address = {}) {
  const countryCode = resolveCountryCode(address);
  const country = resolveCountryLabel(address, countryCode);
  const intlCountryName = resolveInternationalCountryName(address);
  const province = countryCode === 'CA'
    ? normalizeCanadianProvince(address.province)
    : (address.province || '');

  return {
    ...address,
    countryCode,
    country: countryCode === 'INTL' ? intlCountryName : country,
    countryName: countryCode === 'INTL' ? intlCountryName : country,
    province,
    state: countryCode === 'US' ? (address.state || address.province || '') : (address.state || ''),
    region: countryCode === 'INTL' ? (address.region || address.province || address.state || '') : (address.region || ''),
  };
}

/** True when a profile/API user record already has a saved street address */
export function hasProfileShippingAddress(profile) {
  if (!profile) return false;
  const line1 = profile.address_line1 || profile.addressLine1 || profile.street || '';
  return !!String(line1).trim();
}

/**
 * Map a user profile (API or localStorage) into checkout/profile address fields.
 * Avoids stale default countryCode (e.g. CA) overriding a saved US address.
 */
export function profileToShippingAddress(profile = {}, fallback = {}) {
  const country = (
    profile.country
    || profile.countryName
    || fallback.country
    || ''
  ).trim();

  const stateProvince = (
    profile.state_province
    || profile.state
    || profile.province
    || profile.region
    || fallback.state
    || fallback.province
    || fallback.region
    || ''
  ).trim();

  const raw = {
    street: profile.address_line1 || profile.addressLine1 || profile.street || fallback.street || '',
    street2: profile.address_line2 || profile.addressLine2 || profile.street2 || fallback.street2 || '',
    city: profile.city || fallback.city || '',
    postalCode: profile.postal_code || profile.postalCode || fallback.postalCode || '',
    country,
    countryName: profile.countryName || country,
    // Derive from profile country — never inherit stale checkout defaults
    countryCode: profile.countryCode || undefined,
    province: '',
    state: '',
    region: '',
  };

  const countryCode = resolveCountryCode(raw);

  if (countryCode === 'CA') {
    raw.province = normalizeCanadianProvince(stateProvince);
  } else if (countryCode === 'US') {
    raw.state = stateProvince;
  } else if (stateProvince) {
    raw.region = stateProvince;
  }

  return normalizeAddress(raw);
}
